import { useEffect, useMemo, useState } from 'react';

import { DeliveryBlockResource } from '@bringg/types';

import { useStores } from 'bringg-web/recipes';

const useSelectedResources = (
	teamId: number,
	unavailableDriverIds: Set<number>,
	unavailableVehicleIds: Set<number>,
	unavailableTrailerIds: Set<number>,
	deliveryBlockTimeRange: { startTime: string; endTime: string },
	onUnavailableDriversChange: (unavailableDrivers: number) => void,
	onUnavailableVehiclesChange: (unavailableVehicles: number) => void,
	onUnavailableTrailersChange: (unavailableTrailers: number) => void
) => {
	const { vehiclesStore } = useStores();
	const [selectedDrivers, setSelectedDrivers] = useState<Set<number>>(new Set());
	const [selectedVehicles, setSelectedVehicles] = useState<Set<number>>(new Set());
	const [selectedTrailers, setSelectedTrailers] = useState<Set<number>>(new Set());

	const unavailableSelectedDrivers = useMemo(() => {
		return Array.from(selectedDrivers).filter(driverId => {
			return unavailableDriverIds.has(driverId);
		}).length;
	}, [selectedDrivers]);

	const unavailableSelectedVehicles = useMemo(() => {
		return Array.from(selectedVehicles).filter(vehicleId => {
			return unavailableVehicleIds.has(vehicleId);
		}).length;
	}, [selectedVehicles]);

	const unavailableSelectedTrailers = useMemo(() => {
		return Array.from(selectedTrailers).filter(trailerId => {
			return unavailableTrailerIds.has(trailerId);
		}).length;
	}, [selectedTrailers]);

	useEffect(() => {
		onUnavailableDriversChange(unavailableSelectedDrivers);
	}, [unavailableSelectedDrivers]);

	useEffect(() => {
		onUnavailableVehiclesChange(unavailableSelectedVehicles);
	}, [unavailableSelectedVehicles]);

	useEffect(() => {
		onUnavailableTrailersChange(unavailableSelectedTrailers);
	}, [unavailableSelectedTrailers]);

	function updateSelected(resources: DeliveryBlockResource[]) {
		const newSelectedVehicles = new Set(resources.map(resource => resource.vehicle_id).filter(Boolean));
		const newSelectedDrivers = new Set(resources.map(resource => resource.user_id).filter(Boolean));
		const newSelectedTrailers = new Set(
			resources
				.map(resource => {
					if (resource.vehicle_id && resource.use_default_trailer) {
						return vehiclesStore.get(resource.vehicle_id)?.trailer?.id;
					}
					return resource.trailer_id;
				})
				.filter(Boolean)
		);

		const areSetsEqual = (a, b) => a.size === b.size && [...a].every(value => b.has(value));

		if (!areSetsEqual(selectedVehicles, newSelectedVehicles)) {
			setSelectedVehicles(newSelectedVehicles);
		}

		if (!areSetsEqual(selectedDrivers, newSelectedDrivers)) {
			setSelectedDrivers(newSelectedDrivers);
		}

		if (!areSetsEqual(selectedTrailers, newSelectedTrailers)) {
			setSelectedTrailers(newSelectedTrailers);
		}
	}

	return {
		selectedDrivers,
		selectedVehicles,
		selectedTrailers,
		setSelectedDrivers,
		setSelectedVehicles,
		setSelectedTrailers,
		updateSelected
	};
};

export default useSelectedResources;
